/**
 * The settings that belong to a person rather than to a chart.
 *
 * Chart appearance - text size, ramp, point size and opacity, which toggles are
 * on - is taste. Until this existed the only place it could live was the link,
 * so everybody re-set it every morning. Now it is remembered: in this browser
 * immediately, and on the account a moment later when there is a database.
 *
 * **Writes are optimistic, and deliberately so.** The control updates, the chart
 * redraws, `localStorage` is written, and only then - after a debounce - does
 * the server hear about it. Nothing downstream reads a preference
 * transactionally, so the worst case of a failed write is a setting that does
 * not follow you to another laptop. Blocking a checkbox on a round trip to buy
 * that back would be a bad trade.
 *
 * Three things make it safe:
 *
 * - **The mirror is the fallback, not the cache.** `localStorage` is written
 *   first and read at boot, synchronously, so the seeded chart is right before
 *   any request has been made and stays right when every request fails.
 * - **Writes merge, never replace.** A patch carries only what changed, and the
 *   server merges it into the row, so two tabs changing two settings do not take
 *   turns erasing each other.
 * - **A reply never restyles anything.** The response is used for one thing:
 *   whether the write landed, which is the line the Preferences panel shows.
 *   The account copy is adopted exactly once, at boot, by `adoptServerPreferences`
 *   - so a slow reply arriving after the user has moved on cannot undo the move.
 *
 * What this module does *not* do is decide what a preference means. It stores an
 * opaque bag of chart keys; `plot-state.ts` owns the defaults those keys
 * override and the allowlist of which ones are remembered at all.
 */

import { Atom } from './atom.ts';
import { patchPreferences, resetPreferences as resetOnServer } from '../api/endpoints.ts';

export type ThemeName = 'light' | 'dark';

export interface Preferences {
  /** Null means "whatever this browser already does" - no choice recorded. */
  theme: ThemeName | null;
  /** Only the keys the user has actually chosen; the rest are plot-state defaults. */
  chart: Record<string, unknown>;
  /**
   * The lens this person looks through, or null for the plain application.
   * Stored here because it belongs to the person, not to a chart or a link;
   * what it *means* is `state/lens.ts`'s business, not this module's.
   */
  lens: string | null;
}

/**
 * How far these settings actually reach, for the one line the panel shows.
 *
 * `account` - stored server-side, follows you anywhere. `browser` - there is no
 * database, so this browser is as far as it goes (which is the app as it was).
 * `unsaved` - a write failed; the setting is applied and kept locally, and that
 * is worth saying once, in the one place somebody is looking at preferences.
 */
export type PreferenceReach = 'account' | 'browser' | 'unsaved';

const STORAGE_KEY = 'preferences';

/** Long enough to fold a slider drag into one write, short enough to feel like none. */
const FLUSH_DELAY_MS = 500;
const RETRY_DELAYS_MS = [1000, 4000];

function emptyPreferences(): Preferences {
  return { theme: null, chart: {}, lens: null };
}

/** A lens name, or null. Shape only - which lenses exist is not known here. */
function cleanLens(value: unknown): string | null {
  return typeof value === 'string' && /^[a-z][a-z0-9_-]{0,23}$/.test(value) ? value : null;
}

/**
 * Read the mirror. Synchronous and at module load on purpose: the plot state is
 * seeded from it before anything renders, so there is no flash of default and no
 * dependency on a request that may never come back.
 */
function load(): Preferences {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyPreferences();
    const parsed = JSON.parse(raw) as Partial<Preferences>;
    return {
      theme: parsed.theme === 'light' || parsed.theme === 'dark' ? parsed.theme : null,
      chart:
        parsed.chart && typeof parsed.chart === 'object' ? { ...parsed.chart } : {},
      lens: cleanLens(parsed.lens),
    };
  } catch {
    // A corrupt or unavailable store is not worth a broken boot.
    return emptyPreferences();
  }
}

function save(value: Preferences): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Private mode, or a full quota. The setting still applies to this session.
  }
}

export const preferences = new Atom<Preferences>(load());
export const preferenceReach = new Atom<PreferenceReach>('browser');

/** Pending changes not yet flushed, coalesced across every edit in the window. */
let pending: {
  theme?: ThemeName | null;
  chart: Record<string, unknown>;
  /** Null is "take the lens off", so `lensSet` is what carries "mentioned". */
  lens?: string | null;
  lensSet?: boolean;
} = { chart: {} };
let timer: ReturnType<typeof setTimeout> | null = null;
let writeSeq = 0;
/** True once this browser has made an edit; the server copy stops overriding it. */
let editedLocally = false;

/** Whether anything is waiting to be written (used by the tests and the panel). */
export function hasPendingWrites(): boolean {
  return timer !== null;
}

function scheduleFlush(): void {
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => {
    timer = null;
    void flush();
  }, FLUSH_DELAY_MS);
}

async function flush(attempt = 0): Promise<void> {
  const body = pending;
  pending = { chart: {} };
  if (body.theme === undefined && !Object.keys(body.chart).length && !body.lensSet) return;
  const seq = ++writeSeq;
  try {
    const resolved = await patchPreferences({
      ...(body.theme !== undefined ? { theme: body.theme } : {}),
      chart: body.chart,
      ...(body.lensSet ? { lens: body.lens ?? null, lens_set: true } : {}),
    });
    // Only how far it reached, and only if this is still the newest write. The
    // values themselves are never taken from a write reply: the client already
    // has them, and applying a reply that a later edit has overtaken would
    // restyle the chart back to a setting the user has just changed.
    if (seq !== writeSeq) return;
    preferenceReach.set(resolved.stored ? 'account' : 'browser');
  } catch {
    if (attempt < RETRY_DELAYS_MS.length) {
      // Put the change back at the front of the queue and try again; a later
      // edit merges on top of it rather than being lost behind it.
      pending = { ...body, ...pending, chart: { ...body.chart, ...pending.chart } };
      setTimeout(() => void flush(attempt + 1), RETRY_DELAYS_MS[attempt]);
      return;
    }
    // Applied, mirrored, not synced. Said once, where preferences are read.
    preferenceReach.set('unsaved');
  }
}

/**
 * Record a change: applied and mirrored now, sent shortly.
 *
 * `chart` is merged key by key, so a caller sends the one setting it changed.
 */
export function rememberPreference(patch: {
  theme?: ThemeName | null;
  chart?: Record<string, unknown>;
}): void {
  const next: Preferences = {
    theme: patch.theme !== undefined ? patch.theme : preferences.value.theme,
    chart: { ...preferences.value.chart, ...(patch.chart ?? {}) },
    lens: preferences.value.lens,
  };
  editedLocally = true;
  preferences.set(next);
  save(next);
  if (patch.theme !== undefined) pending.theme = patch.theme;
  if (patch.chart) pending.chart = { ...pending.chart, ...patch.chart };
  scheduleFlush();
}

/**
 * Remember which lens this person looks through - `null` for none.
 *
 * Separate from `rememberPreference` because null is a real value here rather
 * than "unchanged", and because `state/lens.ts` is the only caller: this module
 * stores the name and never asks what it means.
 */
export function setLensPreference(lens: string | null): void {
  const next: Preferences = { ...preferences.value, lens };
  editedLocally = true;
  preferences.set(next);
  save(next);
  pending.lens = lens;
  pending.lensSet = true;
  scheduleFlush();
}

/** Replace the local copy with the server's, mirror included. */
function adopt(payload: {
  theme?: ThemeName | null;
  chart?: Record<string, unknown>;
  lens?: string | null;
}): void {
  const next: Preferences = {
    theme: payload.theme === 'light' || payload.theme === 'dark' ? payload.theme : null,
    chart: { ...(payload.chart ?? {}) },
    lens: cleanLens(payload.lens),
  };
  preferences.set(next);
  save(next);
}

/**
 * Take what `/api/me` carried.
 *
 * Returns whether the caller should apply it to what is already on screen. It
 * should not, once this browser has an opinion of its own: adopting then would
 * mean a chart restyling itself seconds after it was drawn, which reads as a
 * bug however correct the value is. A fresh browser has nothing to protect, so
 * there it is applied and the new laptop looks like the old one.
 */
export function adoptServerPreferences(payload: unknown): boolean {
  const body = payload as
    | {
        theme?: ThemeName | null;
        chart?: Record<string, unknown>;
        lens?: string | null;
        stored?: boolean;
      }
    | undefined;
  if (!body || typeof body !== 'object') return false;
  preferenceReach.set(body.stored ? 'account' : 'browser');
  // Nothing is stored for this account, so there is nothing to take. Copying the
  // resolved defaults into the mirror would leave every browser claiming the
  // user had chosen all fifteen settings, and "nothing stored yet" would never
  // be true again.
  if (!body.stored) return false;
  if (editedLocally || hasPendingWrites()) return false;
  const hadLocal = Object.keys(preferences.value.chart).length > 0 || preferences.value.theme;
  adopt(body);
  return !hadLocal;
}

/** Forget everything - on the account and in this browser - and say what is left. */
export async function resetPreferences(): Promise<void> {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  pending = { chart: {} };
  writeSeq++;
  editedLocally = false;
  preferences.set(emptyPreferences());
  save(emptyPreferences());
  try {
    const resolved = await resetOnServer();
    preferenceReach.set(resolved.stored ? 'account' : 'browser');
  } catch {
    preferenceReach.set('unsaved');
  }
}

/** Test seam: drop every local trace, as if this browser had never been used. */
export function clearPreferencesForTests(): void {
  if (timer) clearTimeout(timer);
  timer = null;
  pending = { chart: {} };
  writeSeq = 0;
  editedLocally = false;
  preferences.set(emptyPreferences());
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to clear */
  }
}
